import {
  Component,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useId,
  type ReactNode,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import { ConfigProvider, Typography } from "antd";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";
import type { RichTextEditorProps } from "./types";
import { getExtensions } from "./extensions";
import { Toolbar } from "./toolbar";
import {
  EMPTY_EDITOR_HTML,
  normalizeEditorHtml,
  countWords,
  getTextFromHtml,
} from "./utils";
import { useEditorMediaUpload } from "./hooks/useEditorMediaUpload";
import "./styles.css";

const { Text } = Typography;

const sizeConfig = {
  small: { fontSize: "0.875rem", padding: 10 },
  medium: { fontSize: "0.9375rem", padding: 12 },
  large: { fontSize: "1rem", padding: 16 },
} as const;

const containsMediaHtml = (html: string) =>
  /data-type=["']resizable-media["']|data-media-type=|<(img|video)\b|class=["'][^"']*\bdocument-link\b/i.test(
    html,
  );

interface EditorSnapshot {
  normalizedHtml: string;
  plainText: string;
}

interface RichTextEditorErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface RichTextEditorErrorBoundaryState {
  hasError: boolean;
}

class RichTextEditorErrorBoundary extends Component<
  RichTextEditorErrorBoundaryProps,
  RichTextEditorErrorBoundaryState
> {
  state: RichTextEditorErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RichTextEditorErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("RichTextEditor failed to initialize", error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const RichTextEditorErrorFallback = ({
  className = "",
  fullWidth = true,
  label,
  required,
  helperText,
  error,
  variant = "filled",
}: Pick<
  RichTextEditorProps,
  "className" | "fullWidth" | "label" | "required" | "helperText" | "error" | "variant"
>) => {
  const borderColor = error ? colors.error : colors.surfaceSubtle;
  const backgroundColor =
    variant === "borderless"
      ? colors.transparent
      : variant === "filled"
        ? colors.surfaceLighter
        : colors.white;

  return (
    <div className={cn(fullWidth && "w-full", className)}>
      {label && (
        <label
          className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          <span>{label}</span>
          {required && <span style={{ color: colors.error }}>*</span>}
        </label>
      )}

      <div
        className="rich-text-editor"
        style={{
          borderColor,
          borderWidth: variant === "borderless" ? 0 : 1,
          borderStyle: "solid",
          backgroundColor,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          className="rich-text-editor-content"
          style={{ minHeight: 120, padding: 12 }}
        >
          <Text type="danger">
            The editor could not be loaded. Refresh the page and try again.
          </Text>
        </div>
      </div>

      {helperText && !error && (
        <div className="mt-1 text-xs" style={{ color: colors.textMuted }}>
          {helperText}
        </div>
      )}
      {error && (
        <div className="mt-1 text-xs" style={{ color: colors.error }}>
          {error}
        </div>
      )}
    </div>
  );
};

const buildEditorSnapshot = (html: string): EditorSnapshot => {
  const normalizedHtml = normalizeEditorHtml(html);
  return {
    normalizedHtml,
    plainText: getTextFromHtml(normalizedHtml),
  };
};

const RichTextEditorInner = ({
  value,
  onChange,
  placeholder = "Start writing...",
  disabled = false,
  maxLength,
  minHeight = 300,
  maxHeight,
  autoFocus = false,
  readOnly = false,
  showWordCount = false,
  showCharCount = false,
  className = "",
  mentionUsers = [],
  label,
  required = false,
  helperText,
  error,
  onMediaDelete,
  onMediaUpload,
  onImageUpload,
  size = "medium",
  variant = "filled",
  fullWidth = true,
  popupZIndex,
  toolbarVariant = "default",
  hideToolbar = false,
  contentFontFamily,
  onEditorReady,
  mediaFileConfig,
  onStorageUrlsUploaded,
  onContentFilesUploaded,
  contentFileUsage,
  disallowDataAndBlobMediaUrls = false,
  requireMediaDescription = false,
}: RichTextEditorProps) => {
  const generatedId = useId();
  const editorId = `rich-text-editor-${generatedId}`;
  const labelId = label ? `${editorId}-label` : undefined;
  const helperTextId = helperText && !error ? `${editorId}-helper` : undefined;
  const errorId = error ? `${editorId}-error` : undefined;
  const describedBy =
    [helperTextId, errorId].filter(Boolean).join(" ") || undefined;

  const initialSnapshot = useMemo(() => buildEditorSnapshot(value), [value]);

  const editorRef = useRef<Editor | null>(null);
  const { handleMediaUpload, handleMediaDelete } = useEditorMediaUpload({
    deferDeleteToParent: Boolean(onStorageUrlsUploaded),
    onStorageUrlsUploaded,
    onContentFilesUploaded,
    contentFileUsage,
  });
  const handleCombinedMediaDelete = useCallback(
    (src: string) => {
      handleMediaDelete(src);
      onMediaDelete?.(src);
    },
    [handleMediaDelete, onMediaDelete],
  );

  const uploadHandler = onMediaUpload || onImageUpload || handleMediaUpload;
  const pendingUpdate = useRef(false);
  const lastEmittedHtml = useRef<string>(initialSnapshot.normalizedHtml);
  const pendingDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingDebouncedChangeRef = useRef<{
    html: string;
    json: object;
  } | null>(null);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [, setEditorState] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [snapshot, setSnapshot] = useState<EditorSnapshot>(initialSnapshot);

  const updateSnapshot = useCallback((nextSnapshot: EditorSnapshot) => {
    setSnapshot((previousSnapshot) =>
      previousSnapshot.normalizedHtml === nextSnapshot.normalizedHtml &&
      previousSnapshot.plainText === nextSnapshot.plainText
        ? previousSnapshot
        : nextSnapshot,
    );
  }, []);

  const forceToolbarUpdate = useCallback(() => {
    setEditorState((prev) => prev + 1);
  }, []);

  const externalSnapshot = useMemo(() => buildEditorSnapshot(value), [value]);
  const externalNormalizedHtml = externalSnapshot.normalizedHtml;

  const isFilled = snapshot.normalizedHtml !== EMPTY_EDITOR_HTML;

  const getBorderColor = () => {
    if (variant === "borderless") return "transparent";
    if (error) return colors.error;
    if (isFocused) return colors.primary;
    if (isFilled) return colors.surfaceDark;
    return colors.surfaceSubtle;
  };

  const getBackgroundColor = () => {
    if (variant === "borderless") return "transparent";
    if (disabled) return colors.surfaceLighter;
    if (isFocused) return colors.white;
    return variant === "filled" ? colors.surfaceLighter : colors.white;
  };

  const getBorderWidth = () => {
    if (variant === "borderless") return 0;
    return isFocused || error ? 2 : 1;
  };

  const config = sizeConfig[size];

  const extensions = useMemo(
    () =>
      getExtensions({
        placeholder,
        maxLength,
        uploadFn: uploadHandler,
        onMediaDelete: handleCombinedMediaDelete,
        mentionUsers,
      }),
    [
      placeholder,
      maxLength,
      uploadHandler,
      handleCombinedMediaDelete,
      mentionUsers,
    ],
  );

  const emitEditorChange = useCallback(
    (html: string, json: object) => {
      const nextSnapshot = buildEditorSnapshot(html);
      updateSnapshot(nextSnapshot);

      if (nextSnapshot.normalizedHtml === lastEmittedHtml.current) {
        pendingUpdate.current = false;
        return;
      }

      lastEmittedHtml.current = nextSnapshot.normalizedHtml;
      onChangeRef.current(nextSnapshot.normalizedHtml, json);
      pendingUpdate.current = false;
    },
    [updateSnapshot],
  );

  const cancelPendingDebouncedChange = useCallback(() => {
    if (pendingDebounceTimeoutRef.current) {
      clearTimeout(pendingDebounceTimeoutRef.current);
      pendingDebounceTimeoutRef.current = null;
    }
  }, []);

  const flushPendingDebouncedChange = useCallback(() => {
    const pendingChange = pendingDebouncedChangeRef.current;
    cancelPendingDebouncedChange();
    if (!pendingChange) return;

    pendingDebouncedChangeRef.current = null;
    emitEditorChange(pendingChange.html, pendingChange.json);
  }, [cancelPendingDebouncedChange, emitEditorChange]);

  const scheduleDebouncedEditorChange = useCallback(
    (html: string, json: object) => {
      pendingDebouncedChangeRef.current = { html, json };
      cancelPendingDebouncedChange();
      pendingDebounceTimeoutRef.current = setTimeout(() => {
        flushPendingDebouncedChange();
      }, 300);
    },
    [cancelPendingDebouncedChange, flushPendingDebouncedChange],
  );

  const editor = useEditor({
    extensions,
    content: initialSnapshot.normalizedHtml,
    editable: !disabled && !readOnly,
    autofocus: autoFocus,
    onUpdate: ({ editor: currentEditor }) => {
      pendingUpdate.current = true;
      const html = currentEditor.getHTML();
      const json = currentEditor.getJSON();
      if (containsMediaHtml(html)) {
        pendingDebouncedChangeRef.current = null;
        cancelPendingDebouncedChange();
        emitEditorChange(html, json);
      } else {
        scheduleDebouncedEditorChange(html, json);
      }
      forceToolbarUpdate();
    },
    onSelectionUpdate: () => {
      forceToolbarUpdate();
    },
    onTransaction: () => {
      forceToolbarUpdate();
    },
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      flushPendingDebouncedChange();
      setIsFocused(false);
    },
    editorProps: {
      transformPastedHTML: (html) => normalizeEditorHtml(html),
      attributes: {
        id: editorId,
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl focus:outline-none",
        role: "textbox",
        "aria-multiline": "true",
        "aria-labelledby": labelId,
        "aria-describedby": describedBy,
        "aria-invalid": error ? "true" : "false",
        "aria-required": required ? "true" : "false",
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
    onEditorReady?.(editor);

    return () => {
      flushPendingDebouncedChange();
      if (editorRef.current === editor) {
        onEditorReady?.(null);
      }
    };
  }, [editor, flushPendingDebouncedChange, onEditorReady]);

  useEffect(() => {
    let cancelled = false;

    if (
      editor &&
      externalNormalizedHtml !== lastEmittedHtml.current &&
      !pendingUpdate.current
    ) {
      editor.commands.setContent(externalNormalizedHtml, false);
      lastEmittedHtml.current = externalNormalizedHtml;
    }

    if (snapshot.normalizedHtml !== externalNormalizedHtml) {
      queueMicrotask(() => {
        if (!cancelled) {
          updateSnapshot(externalSnapshot);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [editor, externalNormalizedHtml, externalSnapshot, snapshot.normalizedHtml, updateSnapshot]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly);
    }
  }, [disabled, readOnly, editor]);

  useEffect(() => {
    const editorElement = editor?.view.dom;
    if (!editorElement) return;

    if (contentFontFamily) {
      editorElement.style.setProperty(
        "font-family",
        contentFontFamily,
        "important",
      );
    } else {
      editorElement.style.removeProperty("font-family");
    }
  }, [contentFontFamily, editor]);

  const characterCount = editor?.storage.characterCount?.characters() ?? 0;
  const wordCount = countWords(snapshot.plainText);

  const containerStyle: React.CSSProperties = {
    borderColor: getBorderColor(),
    borderWidth: getBorderWidth(),
    borderStyle: "solid",
    backgroundColor: getBackgroundColor(),
    borderRadius: 3,
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    overflow: "hidden",
  };

  const editorContentStyle: React.CSSProperties = {
    minHeight,
    ...(maxHeight ? { maxHeight, overflowY: "auto" as const } : {}),
    fontSize: config.fontSize,
    padding: config.padding,
  };

  return (
    <div className={cn(fullWidth && "w-full", className)}>
      {label && (
        <label
          id={labelId}
          className="inline-flex items-center gap-1 mb-1.5 text-sm font-medium"
          style={{ color: colors.textDefault }}
        >
          <span>{label}</span>
          {required && <span style={{ color: colors.error }}>*</span>}
        </label>
      )}

      <div
        className={cn(
          "rich-text-editor",
          disabled && "disabled",
          readOnly && "readonly",
        )}
        style={containerStyle}
      >
        {!readOnly && !hideToolbar && (
          <ConfigProvider
            theme={
              popupZIndex
                ? { token: { zIndexPopupBase: popupZIndex } }
                : undefined
            }
          >
            <Toolbar
              editor={editor}
              disabled={disabled}
              onMediaUpload={uploadHandler}
              popupZIndex={popupZIndex}
              variant={toolbarVariant}
              mediaFileConfig={mediaFileConfig}
              disallowDataAndBlobMediaUrls={disallowDataAndBlobMediaUrls}
              requireMediaDescription={requireMediaDescription}
            />
          </ConfigProvider>
        )}

        <div className="rich-text-editor-content" style={editorContentStyle}>
          <EditorContent editor={editor} />
        </div>

        {(showWordCount || showCharCount) && (
          <div className="rich-text-editor-footer">
            {showWordCount && (
              <div className="count-item">
                <Text type="secondary">{wordCount} words</Text>
              </div>
            )}
            {showCharCount && (
              <div className="count-item">
                <Text type="secondary">
                  {characterCount}
                  {maxLength ? ` / ${maxLength}` : ""} characters
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {helperText && !error && (
        <div
          id={helperTextId}
          className="mt-1 text-xs"
          style={{ color: colors.textMuted }}
        >
          {helperText}
        </div>
      )}
      {error && (
        <div
          id={errorId}
          className="mt-1 text-xs"
          style={{ color: colors.error }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export const RichTextEditor = (props: RichTextEditorProps) => (
  <RichTextEditorErrorBoundary
    fallback={
      <RichTextEditorErrorFallback
        className={props.className}
        fullWidth={props.fullWidth}
        label={props.label}
        required={props.required}
        helperText={props.helperText}
        error={props.error}
        variant={props.variant}
      />
    }
  >
    <RichTextEditorInner {...props} />
  </RichTextEditorErrorBoundary>
);

export default RichTextEditor;
